import { array, boolean, date, mixed, number, object, string } from "yup";

import { ASSETS_FAMILY, AuctionStatus } from "@utils";
import {
  MAX_CONVERSION_RATE_VALUE,
  MAX_DEPOSIT_VALUE,
  MAX_FEE_VALUE,
  MIN_DEPOSIT_VALUE,
  MIN_FEE_VALUE,
  MIN_NON_WFP_LOTS_VALUE,
  MIN_UNHCR_SERVICES_VALUE,
} from "../CreateAuctionForm";
import { TUpdateAuctionFormValues } from "./UpdateAuctionForm";

const MAX_50MB = 50 * 1024 * 1024; // 50MB in bytes
const ALLOWED_FILE_TYPES = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];

const isAllowedType = (file: File | null): boolean => {
  if (!file) return true; // Allow null/undefined files
  const fileName = file.name.toLowerCase();
  return ALLOWED_FILE_TYPES.some((ext) => fileName.endsWith(ext));
};

const personalDataSchema = object({
  fullName: string(),
  email: string().email("errors.email.invalid"),
});

const conversionRateSchema = number()
  .positive("auctionForm.errors.conversionRate.min")
  .max(MAX_CONVERSION_RATE_VALUE, "auctionForm.errors.conversionRate.max")
  .test(
    "maxDigitsAfterDecimal",
    "auctionForm.errors.conversionRate.decimals",
    (number) =>
      number ? /^\d+(\.\d{1,3})?$/.test(number?.toString() ?? "") : true
  );

export const VALIDATION_SCHEMA = (status: AuctionStatus) => {
  if (status === AuctionStatus.DONE || status === AuctionStatus.LIVE) {
    return object({
      conversionRate: conversionRateSchema,
      deposits: array()
        .required("auctionForm.errors.deposit.required")
        .of(
          object({
            assets: array().of(string()),
            deposit: number()
              .min(MIN_DEPOSIT_VALUE, "auctionForm.errors.deposit.min")
              .max(MAX_DEPOSIT_VALUE, "auctionForm.errors.deposit.max"),
          })
        ),
      unhcrServicesFee: number()
        .min(
          MIN_UNHCR_SERVICES_VALUE,
          "auctionForm.errors.unhcrServicesFee.min"
        )
        .test(
          "maxDigitsAfterDecimal",
          "auctionForm.errors.unhcrServicesFee.decimals",
          (number) =>
            number ? /^\d+(\.\d{1,2})?$/.test(number?.toString() ?? "") : true
        ),
      nonWfpLots: number()
        .positive("auctionForm.errors.nonWfpLots.min")
        .min(MIN_NON_WFP_LOTS_VALUE, "auctionForm.errors.nonWfpLots.min"),
      auctioneerInvoice: number()
        .min(
          MIN_UNHCR_SERVICES_VALUE,
          "auctionForm.errors.auctioneerInvoice.min"
        )
        .test(
          "maxDigitsAfterDecimal",
          "auctionForm.errors.auctioneerInvoice.decimals",
          (number) =>
            number ? /^\d+(\.\d{1,2})?$/.test(number?.toString() ?? "") : true
        ),
    });
  } else {
    return object({
      country: number().required("errors.country.required"),
      city: number().required("errors.city.required"),
      date: date()
        .when("isOnline", {
          is: true,
          then: date().min(new Date(), "errors.date.later"),
        })
        .required("errors.date.required"),
      currency: string().required("errors.currency.required"),
      conversionRate: conversionRateSchema,
      auctioneerFees: array()
        .required("auctionForm.errors.fee.required")
        .of(
          object({
            assets: array().of(string()),
            fee: number()
              .min(MIN_FEE_VALUE, "auctionForm.errors.fee.min")
              .max(MAX_FEE_VALUE, "auctionForm.errors.fee.max"),
          })
        ),
      deposits: array()
        .required("auctionForm.errors.deposit.required")
        .of(
          object({
            assets: array().of(string()),
            deposit: number()
              .min(MIN_DEPOSIT_VALUE, "auctionForm.errors.deposit.min")
              .max(MAX_DEPOSIT_VALUE, "auctionForm.errors.deposit.max"),
          })
        ),
      unhcrServicesFee: number()
        .min(
          MIN_UNHCR_SERVICES_VALUE,
          "auctionForm.errors.unhcrServicesFee.min"
        )
        .test(
          "maxDigitsAfterDecimal",
          "auctionForm.errors.unhcrServicesFee.decimals",
          (number) =>
            number ? /^\d+(\.\d{1,2})?$/.test(number?.toString() ?? "") : true
        ),
      nonWfpLots: number()
        .positive("auctionForm.errors.nonWfpLots.min")
        .min(MIN_NON_WFP_LOTS_VALUE, "auctionForm.errors.nonWfpLots.min"),
      auctioneerInvoice: number()
        .min(
          MIN_UNHCR_SERVICES_VALUE,
          "auctionForm.errors.auctioneerInvoice.min"
        )
        .test(
          "maxDigitsAfterDecimal",
          "auctionForm.errors.auctioneerInvoice.decimals",
          (number) =>
            number ? /^\d+(\.\d{1,2})?$/.test(number?.toString() ?? "") : true
        ),
      supervisor: personalDataSchema,
      focalPoint: personalDataSchema,
      representative: personalDataSchema,
      customDeposit: boolean(),
      customDepositName: string().when("customDeposit", {
        is: true,
        then: (s) =>
          s.trim().required("auctionForm.errors.customDepositName.required"),
        otherwise: (s) => s.notRequired(),
      }),
      depositAttachment: mixed<File | null>().when("customDeposit", {
        is: true,
        then: (m) =>
          m
            .test(
              "file-or-existing",
              "auctionForm.errors.depositAttachment.required",
              function (file) {
                const { custom_deposit_file } = this.parent;
                // If there's an existing file URL, the field is valid even without a new file
                if (custom_deposit_file && custom_deposit_file.url) {
                  return true;
                }
                // Otherwise, require a new file
                return file != null;
              }
            )
            .test("file-type", "auctionForm.errors.file.format", (file) =>
              isAllowedType(file as File | null)
            )
            .test(
              "file-size",
              "auctionForm.errors.file.max50mb",
              (file) => !file || (file as File).size <= MAX_50MB
            ),
        otherwise: (m) => m.nullable().notRequired(),
      }),
    });
  }
};

export const validateForm = (values: TUpdateAuctionFormValues) => {
  const errors: any = {};

  if (!values.isOnline) {
    if (
      values.auctioneerFees.flatMap((auctionGroup) => auctionGroup.assets)
        .length !== ASSETS_FAMILY.length
    ) {
      errors.auctioneerFees = "auctionForm.errors.fee.invalid";
    }

    if (
      values.deposits.flatMap((depositGroup) => depositGroup.assets).length !==
      ASSETS_FAMILY.length
    ) {
      errors.deposit = "auctionForm.errors.deposit.invalid";
    }
  }

  if (values.isOnline && values.requiresOnlineDeposits) {
    if (
      values.deposits.flatMap((depositGroup) => depositGroup.assets).length !==
      ASSETS_FAMILY.length
    ) {
      errors.deposit = "auctionForm.errors.deposit.invalid";
    }
  }

  if (values.supervisor) {
    const { fullName, email, phone } = values.supervisor;
    const fieldsFilled = [fullName, email, phone].filter(Boolean).length;
    if (fieldsFilled > 0 && fieldsFilled < 3) {
      if (!fullName) {
        errors.supervisor = errors.supervisor || {};
        errors.supervisor.fullName =
          "auctionForm.errors.supervisor.fullNameRequired";
      }
      if (!email) {
        errors.supervisor = errors.supervisor || {};
        errors.supervisor.email = "auctionForm.errors.supervisor.emailRequired";
      }
      if (!phone) {
        errors.supervisor = errors.supervisor || {};
        errors.supervisor.phone = "auctionForm.errors.supervisor.phoneRequired";
      }
    }
  }
  if (values.focalPoint) {
    const { fullName, email, phone } = values.focalPoint;
    const fieldsFilled = [fullName, email, phone].filter(Boolean).length;
    if (fieldsFilled > 0 && fieldsFilled < 3) {
      if (!fullName) {
        errors.focalPoint = errors.focalPoint || {};
        errors.focalPoint.fullName =
          "auctionForm.errors.supervisor.fullNameRequired";
      }
      if (!email) {
        errors.focalPoint = errors.focalPoint || {};
        errors.focalPoint.email = "auctionForm.errors.supervisor.emailRequired";
      }
      if (!phone) {
        errors.focalPoint = errors.focalPoint || {};
        errors.focalPoint.phone = "auctionForm.errors.supervisor.phoneRequired";
      }
    }
  }
  if (values.representative) {
    const { fullName, email, phone } = values.representative;
    const fieldsFilled = [fullName, email, phone].filter(Boolean).length;
    if (fieldsFilled > 0 && fieldsFilled < 3) {
      if (!fullName) {
        errors.representative = errors.representative || {};
        errors.representative.fullName =
          "auctionForm.errors.supervisor.fullNameRequired";
      }
      if (!email) {
        errors.representative = errors.representative || {};
        errors.representative.email =
          "auctionForm.errors.supervisor.emailRequired";
      }
      if (!phone) {
        errors.representative = errors.representative || {};
        errors.representative.phone =
          "auctionForm.errors.supervisor.phoneRequired";
      }
    }
  }

  return errors;
};
